/** A half-finished decision, kept in this browser so a closed tab or a refresh does not lose it
 * (EXP-35, ADR-0112).
 *
 * **What is stored is identifiers and choices, and nothing a person typed in words.** A site, a
 * meter, "mobile or stationary", a share, a set of rows left out: each is validated against a
 * strict shape on the way in and on the way out, and a key that is not on the allow-list for its
 * kind is dropped rather than carried. The free-text a decision may also hold (a rejection reason,
 * a correction's value and reason, a reason for a high-risk row) is **deliberately not stored**: it
 * is where personal data would arrive, and a draft is not worth that. Losing a sentence is a
 * smaller loss than a name left in a shared computer's storage.
 *
 * **Never applied.** Restoring a draft puts values back in the controls and does nothing else. The
 * screen says it restored them and offers to discard them; the person still presses the button.
 *
 * **Scoped to the person.** The key carries the organisation and the user, so a second person on the
 * same machine neither sees nor overwrites the first's draft, and `clearAllDrafts` runs on sign-out.
 * Drafts also expire after `MAX_AGE_DAYS`, because a choice about a document last month is a
 * choice about a document that may since have been decided by somebody else.
 *
 * `localStorage` is a per-viewer convenience: it throws in a private window and with site data
 * blocked, and the screens must work the same either way. Every access is in a `try`; a failed read
 * is "no draft" and a failed write is dropped.
 */

export const DRAFT_PREFIX = "emitiq.draft.v1.";
export const MAX_AGE_DAYS = 14;

export type DraftKind = "review" | "bulk";

export interface DraftScope {
  org_id: string;
  user_id: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHARE = /^\d{1,12}(\.\d{1,10})?$/;
//: The key a spreadsheet's meter group is answered by: `facility_id|category|fuel_or_carrier`
//: (`meterGroupKey`). Categories and carriers are product vocabulary, not text from a file.
const GROUP_KEY = /^[0-9a-f-]{36}\|[a-z0-9_]{1,40}\|[A-Za-z0-9_ .-]{1,60}$/;
const FILE_HASH = /^[0-9a-f]{64}$/;
const MAX_ROWS = 20000;

/** What a review draft may hold. Every field is optional and independently validated. */
export interface ReviewDraft {
  facilityId?: string;
  fuelUse?: "mobile" | "stationary";
  meterId?: string;
  rememberMeter?: boolean;
  multiSite?: boolean;
  share?: string;
}

/** What a spreadsheet draft may hold: rows left out, and meter answers by group. */
export interface BulkDraft {
  left?: number[];
  meters?: Record<string, string>;
}

export function sanitizeReview(raw: unknown): ReviewDraft {
  const input = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  const out: ReviewDraft = {};
  if (typeof input.facilityId === "string" && UUID.test(input.facilityId)) {
    out.facilityId = input.facilityId;
  }
  if (input.fuelUse === "mobile" || input.fuelUse === "stationary") out.fuelUse = input.fuelUse;
  if (typeof input.meterId === "string" && UUID.test(input.meterId)) out.meterId = input.meterId;
  if (input.rememberMeter === true) out.rememberMeter = true;
  if (input.multiSite === true) out.multiSite = true;
  if (typeof input.share === "string" && SHARE.test(input.share)) out.share = input.share;
  // A meter belongs to a site, and a share to a multi-site statement: an answer kept without the
  // thing it answers about would be restored into a control that no longer means it.
  if (out.meterId && !out.facilityId) delete out.meterId;
  if (out.rememberMeter && !out.meterId) delete out.rememberMeter;
  if (out.share && !out.multiSite) delete out.share;
  return out;
}

export function sanitizeBulk(raw: unknown): BulkDraft {
  const input = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  const out: BulkDraft = {};
  if (Array.isArray(input.left)) {
    const rows = input.left.filter(
      (row): row is number => Number.isInteger(row) && row >= 0 && row < MAX_ROWS,
    );
    if (rows.length > 0) out.left = [...new Set(rows)].sort((a, b) => a - b);
  }
  if (typeof input.meters === "object" && input.meters !== null) {
    const kept: Record<string, string> = {};
    for (const [group, meter] of Object.entries(input.meters as Record<string, unknown>)) {
      if (GROUP_KEY.test(group) && typeof meter === "string" && (meter === "none" || UUID.test(meter))) {
        kept[group] = meter;
      }
    }
    if (Object.keys(kept).length > 0) out.meters = kept;
  }
  return out;
}

export function isEmptyDraft(draft: object): boolean {
  return Object.keys(draft).length === 0;
}

export function reviewKey(scope: DraftScope, documentId: string): string | null {
  return UUID.test(documentId) ? keyFor(scope, "review", documentId) : null;
}

/** A spreadsheet draft is about **one file read for one window**: the file's SHA-256, so a different
 * file never receives it, and the window, so the same file compared against another period does
 * not either. The hash is computed in the browser from the bytes and is not secret. */
export function bulkKey(scope: DraftScope, fileHash: string, from: string, to: string): string | null {
  if (!FILE_HASH.test(fileHash)) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return null;
  return keyFor(scope, "bulk", `${fileHash}.${from}.${to}`);
}

function keyFor(scope: DraftScope, kind: DraftKind, id: string): string | null {
  if (!UUID.test(scope.org_id) || !UUID.test(scope.user_id)) return null;
  return `${DRAFT_PREFIX}${kind}.${scope.org_id}.${scope.user_id}.${id}`;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The draft under `key`, sanitised for `kind`; `{}` when there is none, it expired, or it is
 * unreadable. An unreadable or expired entry is removed. */
export function readDraft<K extends DraftKind>(
  key: string | null,
  kind: K,
  now: number = Date.now(),
): K extends "review" ? ReviewDraft : BulkDraft {
  const empty = {} as K extends "review" ? ReviewDraft : BulkDraft;
  if (key === null) return empty;
  const store = storage();
  if (store === null) return empty;
  try {
    const text = store.getItem(key);
    if (text === null) return empty;
    const parsed: unknown = JSON.parse(text);
    const record = typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
    const savedAt = typeof record.savedAt === "number" ? record.savedAt : 0;
    if (record.v !== 1 || now - savedAt > MAX_AGE_DAYS * 86_400_000 || savedAt > now + 60_000) {
      store.removeItem(key);
      return empty;
    }
    return (kind === "review" ? sanitizeReview(record.data) : sanitizeBulk(record.data)) as typeof empty;
  } catch {
    return empty;
  }
}

/** Save `data` (sanitised for `kind` first) under `key`; an empty draft removes the entry. */
export function writeDraft(
  key: string | null,
  kind: DraftKind,
  data: unknown,
  now: number = Date.now(),
): void {
  if (key === null) return;
  const store = storage();
  if (store === null) return;
  try {
    const clean = kind === "review" ? sanitizeReview(data) : sanitizeBulk(data);
    if (isEmptyDraft(clean)) store.removeItem(key);
    else store.setItem(key, JSON.stringify({ v: 1, savedAt: now, data: clean }));
  } catch {
    // Storage full or blocked: the draft is not kept, and nothing else changes.
  }
}

export function clearDraft(key: string | null): void {
  if (key === null) return;
  try {
    storage()?.removeItem(key);
  } catch {
    // Nothing to do: there was nothing readable to remove.
  }
}

/** Every draft this browser holds, for sign-out. Removes only keys under `DRAFT_PREFIX`. */
export function clearAllDrafts(): void {
  const store = storage();
  if (store === null) return;
  try {
    const mine: string[] = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key !== null && key.startsWith(DRAFT_PREFIX)) mine.push(key);
    }
    for (const key of mine) store.removeItem(key);
  } catch {
    // As above.
  }
}

/** SHA-256 of a file's bytes as lower-case hex, or `null` where the platform cannot (an insecure
 * context has no `crypto.subtle`). `null` means *no draft is kept for this file*, not an error. */
export async function fileDigest(file: Blob): Promise<string | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    const bytes =
      typeof file.arrayBuffer === "function"
        ? await file.arrayBuffer()
        : await new Promise<ArrayBuffer>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as ArrayBuffer);
            reader.onerror = () => reject(new Error("the file could not be read"));
            reader.readAsArrayBuffer(file);
          });
    const digest = await subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}
